import { SyncState, VisitDatetimeField, VisitOutcome } from '@axle-health/graphql-schema';

import { COFK_CONTAINER_KEYS } from '../containerKeys';
import { QueueDefinition, RootEntity } from '../types';

/**
 * Care Options for Kids is a pediatric therapy organization (OT / PT / SLP). Its
 * queues follow Pinnacle's, minus episodes, which the org does not have enabled.
 *
 * Every queue is declared in full here, including the ones whose filters happen to
 * match another org's. A queue is per-org configuration, so changing what this org
 * counts means editing this file and no other.
 */

// COFK eval visit service external IDs, the equivalent of Pinnacle's eval anchor.
const EVAL_SERVICE_IDS = [
    'AQgs1igzRC', // COFK OT Feeding Eval
    'VHZXmZF31w', // COFK PT Eval
    'vIumzDoNU0', // COFK SLP Eval
    '2uLY5XM6fr', // COFK SLP Feeding Eval
    'tCSbyJwvFC', // COFK OT Eval
    'ySGjr3EWPH', // COFK HH OT Eval
    'Uk77LWZg9D', // COFK HH PT Eval
    'uEVM2YK1rn', // COFK HH SLP Eval
    '6XfcgxZben', // COFK OASIS OT Eval
    'AeEDs1igzR', // COFK OASIS PT Eval
    'lN7niGAYKI', // COFK OASIS SLP Eval
];

// COFK discipline-discharge visit service external IDs, per discipline.
const DISCIPLINE_DISCHARGE_SERVICE_IDS = [
    'seru8jaGon', // COFK HH OT DC Summary (Non-Billable)
    'Gp1oUcnUYI', // COFK HH PT DC Summary (Non-Billable)
    'eHIY6l8wVq', // COFK HH SLP DC Summary (Non-Billable)
    'xm2oCpeNMW', // ST - COFK SLP D/C (Non-billable)
    'uch2YK1rnl', // OT - COFK OT D/C (Non-billable)
    'EYBhxLZ9ck', // PT - COFK PT D/C (Non-billable)
];

// A completed eval in the same discipline re-admits the patient. The org has no
// ROC equivalent, so the eval codes stand alone here.
const DISCIPLINE_READMISSION_SERVICE_IDS = [...EVAL_SERVICE_IDS];

const POST_DISCHARGE_EXCLUDED_SERVICE_IDS: string[] = [];

// Statewise TAG_ID external IDs for the two strict service-modality preferences a
// patient can hold.
const TELEHEALTH_ONLY_TAG_ID = 'AXs1igzRC6';
const IN_PERSON_ONLY_TAG_ID = 'OIJLhNcSbf';
const STATEWISE_TAG_EXTERNAL_ID_SYSTEM = 'STATEWISE';
const STATEWISE_TAG_EXTERNAL_ID_TYPE = 'TAG_ID';

// Eval visits that still need a clinician or a date.
const EVAL: QueueDefinition<RootEntity.VISIT> = {
    key: COFK_CONTAINER_KEYS.eval,
    label: 'Eval',
    rootEntity: RootEntity.VISIT,
    snoozable: true,
    heavyQuery: false,
    buildFilters: () => ({
        services: { externalIds: { value: { inList: EVAL_SERVICE_IDS } } },
        needsAssignment: { exact: true },
    }),
};

// Active patients carrying at least one visit that still needs assignment.
const PATIENTS_NEEDING_SCHEDULING: QueueDefinition<RootEntity.PATIENT> = {
    key: COFK_CONTAINER_KEYS.patientsNeedingScheduling,
    label: 'Patients Needing Scheduling',
    rootEntity: RootEntity.PATIENT,
    snoozable: true,
    heavyQuery: false,
    buildFilters: () => ({
        isActive: { exact: true },
        hasVisits: {
            needsAssignment: { exact: true },
        },
    }),
};

// Pending visits left behind after a discipline discharge that a later
// same-discipline eval has not re-admitted.
const DISCIPLINE_DISCHARGE_PATIENTS_WITH_PENDING_VISITS: QueueDefinition<RootEntity.VISIT> = {
    key: COFK_CONTAINER_KEYS.disciplineDischargePatientsWithPendingVisits,
    label: 'Discipline Discharge Patients with Pending Visits',
    rootEntity: RootEntity.VISIT,
    snoozable: true,
    heavyQuery: true,
    buildFilters: () => ({
        outcome: { exact: VisitOutcome.NONE },
        NOT: {
            services: {
                externalIds: { value: { inList: POST_DISCHARGE_EXCLUDED_SERVICE_IDS } },
            },
        },
        relatedVisitCondition: {
            exists: true,
            samePatient: true,
            sameDiscipline: true,
            filter: {
                services: {
                    externalIds: { value: { inList: DISCIPLINE_DISCHARGE_SERVICE_IDS } },
                },
                outcome: { exact: VisitOutcome.COMPLETED },
                NOT: {
                    relatedVisitCondition: {
                        exists: true,
                        samePatient: true,
                        sameDiscipline: true,
                        filter: {
                            services: {
                                externalIds: {
                                    value: { inList: DISCIPLINE_READMISSION_SERVICE_IDS },
                                },
                            },
                            outcome: { exact: VisitOutcome.COMPLETED },
                        },
                        timeConstraints: [
                            {
                                field: VisitDatetimeField.EFFECTIVE_DATE,
                                relativeTo: VisitDatetimeField.EFFECTIVE_DATE,
                                gtOffsetMinutes: 0,
                            },
                        ],
                    },
                },
            },
            timeConstraints: [
                {
                    field: VisitDatetimeField.EFFECTIVE_DATE,
                    relativeTo: VisitDatetimeField.EFFECTIVE_DATE,
                    ltOffsetMinutes: 0,
                },
            ],
        },
    }),
};

// Visits whose modality doesn't match the patient's strict preference:
//   - patient holds Telehealth Only  AND  visit is NOT virtual, OR
//   - patient holds In Person Only   AND  visit IS virtual.
// Empty-services visits count as virtual, so require ≥1 service. Preferences
// are matched by TAG_ID external ID (system=STATEWISE, type=TAG_ID).
const VISITS_MISMATCHING_PATIENT_PREFERENCE: QueueDefinition<RootEntity.VISIT> = {
    key: COFK_CONTAINER_KEYS.visitsMismatchingPatientPreference,
    label: 'Visits Mismatching Patient Preference',
    rootEntity: RootEntity.VISIT,
    snoozable: true,
    heavyQuery: false,
    buildFilters: () => ({
        outcome: { exact: VisitOutcome.NONE },
        // Visit must have ≥1 service; otherwise `isVirtual` defaults true and
        // the modality mismatch is meaningless.
        services: { id: { isNull: false } },
        AND: {
            OR: {
                AND: {
                    isVirtual: { exact: false },
                    patient: {
                        hasMatchingAttribute: {
                            externalIds: {
                                system: { exact: STATEWISE_TAG_EXTERNAL_ID_SYSTEM },
                                type: { exact: STATEWISE_TAG_EXTERNAL_ID_TYPE },
                                value: { exact: TELEHEALTH_ONLY_TAG_ID },
                            },
                        },
                    },
                },
                OR: {
                    AND: {
                        isVirtual: { exact: true },
                        patient: {
                            hasMatchingAttribute: {
                                externalIds: {
                                    system: { exact: STATEWISE_TAG_EXTERNAL_ID_SYSTEM },
                                    type: { exact: STATEWISE_TAG_EXTERNAL_ID_TYPE },
                                    value: { exact: IN_PERSON_ONLY_TAG_ID },
                                },
                            },
                        },
                    },
                },
            },
        },
    }),
};

// Pending visits that failed to reach the EMR.
const VISIT_SYNC_ISSUES: QueueDefinition<RootEntity.VISIT> = {
    key: COFK_CONTAINER_KEYS.visitSyncIssues,
    label: 'Visit Sync Issues',
    rootEntity: RootEntity.VISIT,
    snoozable: false,
    heavyQuery: true,
    buildFilters: () => ({
        syncStatus: { state: { exact: SyncState.FAILING } },
        outcome: { exact: VisitOutcome.NONE },
    }),
};

export const CARE_OPTIONS_FOR_KIDS_QUEUES: QueueDefinition[] = [
    EVAL,
    PATIENTS_NEEDING_SCHEDULING,
    DISCIPLINE_DISCHARGE_PATIENTS_WITH_PENDING_VISITS,
    VISITS_MISMATCHING_PATIENT_PREFERENCE,
    VISIT_SYNC_ISSUES,
];
